import {TaxResidency} from '@sollapay/enums';
import {FieldCombobox, FieldSelect, Icon} from '@sollapay/ui/components';
import {getCountryOptions} from '@sollapay/utils';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import type {ReactNode} from 'react';
import type {Control, FieldPath, FieldValues} from 'react-hook-form';

export interface TaxResidencyFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  taxResidencyName: FieldPath<TFieldValues>;
  taxResidencyCountryName: FieldPath<TFieldValues>;
  taxResidencyError?: string;
  taxResidencyCountryError?: string;
  /** i18n prefix for labels/placeholders, e.g. 'view.buyers.addBuyerModal.buyerDetails'. */
  i18nPrefix: string;
  /** Full i18n path for the ISRAEL/OTHER option labels (no `.israel`/`.other` suffix). Defaults to `${i18nPrefix}.taxResidencyOptions`. */
  i18nOptionsPrefix?: string;
  /** When false, skips RHF-level required validation for both fields — e.g. trust creation, which validates via a Zod schema instead. Defaults to true. */
  validateRequired?: boolean;
  /** Extra row item rendered alongside the residency fields (e.g. a PEP toggle). Wraps to its own row once the country field is also visible, rather than cramming three items into one row. */
  extra?: ReactNode;
}

/**
 * Tax residency select + the OTHER-only country field, shared by every buyer-creation form
 * (person and corporate) and by trust creation. Not used on buyer edit — tax residency is
 * locked once a buyer is added.
 */
export function TaxResidencyFields<TFieldValues extends FieldValues>({
  control,
  taxResidencyName,
  taxResidencyCountryName,
  taxResidencyError,
  taxResidencyCountryError,
  i18nPrefix,
  i18nOptionsPrefix,
  validateRequired = true,
  extra
}: TaxResidencyFieldsProps<TFieldValues>) {
  const {t, i18n} = useTranslation('trustAccounts');
  const countryOptions = getCountryOptions(i18n.language);

  const selectedTaxResidency = useWatch({control, name: taxResidencyName});
  const isOtherResidency = selectedTaxResidency === TaxResidency.OTHER;
  const wrapExtraToNewRow = Boolean(extra) && isOtherResidency;

  const optionsPrefix = i18nOptionsPrefix ?? `${i18nPrefix}.taxResidencyOptions`;
  const taxResidencyOptions = [
    {value: TaxResidency.ISRAEL, label: t(`${optionsPrefix}.israel`)},
    {value: TaxResidency.OTHER, label: t(`${optionsPrefix}.other`)}
  ];

  const requiredMessage = validateRequired ? t('common:fields.taxResidency.required') : undefined;
  const countryRequiredMessage = validateRequired
    ? t('common:fields.taxResidencyCountry.required')
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        <Controller
          name={taxResidencyName}
          control={control}
          rules={requiredMessage ? {required: requiredMessage} : undefined}
          render={({field}) => (
            <FieldSelect
              label={t(`${i18nPrefix}.taxResidency`)}
              placeholder={t(`${i18nPrefix}.taxResidencyPlaceholder`)}
              error={taxResidencyError}
              required
              value={field.value as TaxResidency}
              onValueChange={(value: string) => field.onChange(value as TaxResidency)}
              options={taxResidencyOptions}
            />
          )}
        />

        {isOtherResidency && (
          <Controller
            name={taxResidencyCountryName}
            control={control}
            rules={countryRequiredMessage ? {required: countryRequiredMessage} : undefined}
            render={({field}) => (
              <FieldCombobox
                label={t(`${i18nPrefix}.taxResidencyCountry`)}
                placeholder={t(`${i18nPrefix}.taxResidencyCountryPlaceholder`)}
                startAdornment={<Icon name="globe" className="size-4" />}
                error={taxResidencyCountryError}
                required
                options={countryOptions}
                value={field.value as string}
                onValueChange={field.onChange}
              />
            )}
          />
        )}

        {!wrapExtraToNewRow && extra}
      </div>

      {wrapExtraToNewRow && <div className="grid grid-cols-2 gap-6">{extra}</div>}
    </div>
  );
}
